// Builds a supercluster index from report pins and recomputes the visible
// cluster/point features whenever the Google Map viewport settles.
//
// Lessons carried over from the mobile app:
//  - The index is built from the report set ONLY (never from selection) so
//    tapping a pin doesn't churn the spatial index and drop markers.
//  - Viewport is read straight from the map on the `idle` event.

import { useEffect, useMemo, useRef, useState } from "react";
import Supercluster from "supercluster";
import { ReportData } from "../../types/global.d";
import { CLUSTER_CONFIG } from "../../constants";
import {
  boundsToBBox,
  ClusterAggregate,
  combineClusterAggregate,
  initialClusterAggregate,
  MapBBox,
  ReportPointProperties,
} from "./mapClustering";

export type ReportClusterFeature = Supercluster.ClusterFeature<ClusterAggregate>;
export type ReportPointFeature = Supercluster.PointFeature<ReportPointProperties>;
export type ReportFeature = ReportClusterFeature | ReportPointFeature;

type ReportSupercluster = Supercluster<ReportPointProperties, ClusterAggregate>;

interface Viewport {
  bbox: MapBBox;
  zoom: number;
}

export const isClusterFeature = (
  feature: ReportFeature
): feature is ReportClusterFeature =>
  Boolean((feature.properties as any).cluster);

interface UseReportClustersResult {
  index: ReportSupercluster;
  clusters: ReportFeature[];
}

export const useReportClusters = (
  map: google.maps.Map | null,
  reports: ReportData[]
): UseReportClustersResult => {
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build the spatial index from the report set only.
  const index = useMemo<ReportSupercluster>(() => {
    const points: ReportPointFeature[] = reports
      .filter(
        (report) =>
          typeof report.geoposition?.latitude === "number" &&
          typeof report.geoposition?.longitude === "number"
      )
      .map((report) => ({
        type: "Feature",
        properties: { reportId: report._id, status: Number(report.status) },
        geometry: {
          type: "Point",
          coordinates: [
            report.geoposition.longitude,
            report.geoposition.latitude,
          ],
        },
      }));

    const supercluster: ReportSupercluster = new Supercluster<
      ReportPointProperties,
      ClusterAggregate
    >({
      radius: CLUSTER_CONFIG.RADIUS,
      maxZoom: CLUSTER_CONFIG.MAX_ZOOM,
      minPoints: CLUSTER_CONFIG.MIN_POINTS,
      // Aggregate each point's status into the cluster so its color needs no
      // leaf walk at render time.
      map: (props) => initialClusterAggregate(props.status),
      reduce: combineClusterAggregate,
    });
    supercluster.load(points);
    return supercluster;
  }, [reports]);

  // Track the viewport from the map's `idle` event (settles after pan/zoom).
  useEffect(() => {
    if (!map) {
      return;
    }

    const readViewport = () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      if (!bounds || typeof zoom !== "number") {
        return;
      }
      setViewport({ bbox: boundsToBBox(bounds), zoom });
    };

    const handleIdle = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(
        readViewport,
        CLUSTER_CONFIG.IDLE_DEBOUNCE_MS
      );
    };

    const listener = map.addListener("idle", handleIdle);
    // Seed the initial viewport in case the map is already idle.
    readViewport();

    return () => {
      listener.remove();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [map]);

  const clusters = useMemo<ReportFeature[]>(() => {
    if (!viewport) {
      return [];
    }
    const zoom = Math.min(
      Math.round(viewport.zoom),
      CLUSTER_CONFIG.MAX_ZOOM
    );
    return index.getClusters(viewport.bbox, zoom) as ReportFeature[];
  }, [index, viewport]);

  return { index, clusters };
};
